import React, { useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  PaginationState,
  RowPinningState,
  SortingState,
  TableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/common';

interface ExtendTableOptions<T extends Record<string, unknown>>
  extends Omit<
    TableOptions<T>,
    'data' | 'columns' | 'getCoreRowModel' | 'state'
  > {}

const defaultSorting: SortingState = [{ id: 'id', desc: false }];

export function useTanStackTable<T extends Record<string, any>>({
  options,
  tableData,
  columnConfig,
  initialSorting = defaultSorting,
  syncColumns = false,
}: {
  tableData: T[];
  options?: ExtendTableOptions<T>;
  columnConfig: ColumnDef<T, any>[];
  initialSorting?: SortingState;
  /**
   * Use the latest `columnConfig` on every render instead of the copy taken
   * on mount. Turn it on when the columns read values that change after
   * mount (ZLAYT-1439: the My Documents search term used for highlighting).
   * The caller must memoize `columnConfig`.
   */
  syncColumns?: boolean;
}) {
  const [data, setData] = React.useState<T[]>([...tableData]);
  const [columns, setColumns] = React.useState(() => [...columnConfig]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
    columns.map((c) => c.id!)
  );
  const getRowIdFn = React.useMemo(
    () => options?.getRowId ?? ((row: T) => row.id),
    [options]
  );
  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () =>
      data
        ?.map((item, index) => getRowIdFn(item, index, undefined))
        .filter((id) => id !== undefined && id !== null) || [],
    [data, getRowIdFn]
  );
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [rowPinning, setRowPinning] = React.useState<RowPinningState>({
    top: [],
    bottom: [],
  });
  const [rowSelection, setRowSelection] = React.useState({});

  // ===================================================================================================
  // these are custom functions dependent on dnd kit and react-table to handle Drag and Drop events
  const handleDragEndColumn = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex);
      });
    }
  }, []);

  const handleDragEndRow = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setData((data) => {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        return arrayMove(data, oldIndex, newIndex);
      });
    }
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );
  // these are custom functions dependent on dnd kit and react-table to handle Drag and Drop events
  // =========================================================================================

  /**
   * IMPORTANT:
   * When using server-side pagination (manualPagination: true), the backend already returns a single page of data.
   * In that mode we must NOT enable TanStack's `getPaginationRowModel()` because it will "paginate again"
   * by slicing the current page array, which can result in empty tables after a few page changes.
   */
  const isManualPagination = options?.manualPagination === true;

  const table = useReactTable({
    data,
    columns: syncColumns ? columnConfig : columns,
    state: {
      sorting,
      expanded,
      rowPinning,
      columnOrder,
      globalFilter,
      columnFilters,
      pagination,
      rowSelection,
    },
    ...options,
    getRowId: getRowIdFn,
    getRowCanExpand: () => true,
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onRowPinningChange: setRowPinning,
    onColumnOrderChange: setColumnOrder,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(isManualPagination
      ? {}
      : { getPaginationRowModel: getPaginationRowModel() }),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return {
    table,
    dataIds,
    setData,
    sensors,
    tableData: data,
    rowPinning,
    columnOrder,
    globalFilter,
    pagination,
    sorting,
    rowSelection,
    setRowPinning,
    setColumnOrder,
    setGlobalFilter,
    setPagination,
    setSorting,
    setColumns,
    handleDragEndRow,
    handleDragEndColumn,
  };
}
